/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  headerActions?: React.ReactNode;
}

function DataTable<T>({
  data,
  columns,
  total,
  page,
  limit,
  onPageChange,
  isLoading,
  search,
  onSearchChange,
  headerActions,
}: DataTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4 md:space-y-6 w-full">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
        <div className="flex flex-wrap gap-2 items-center w-full md:w-auto">
          {headerActions}
        </div>
        <Input
          placeholder="Buscar..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full md:max-w-xs rounded-full bg-(--buscador-bg) border-2 border-foreground shadow-sm focus-visible:ring-focus-ring"
        />
      </div>
      <Table className="min-w-[1200px]\">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} className="whitespace-nowrap">
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="py-6 text-center">
                Cargando...
              </TableCell>
            </TableRow>
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn("odd:bg-(--primary) even:bg-(--primary)/50")}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="whitespace-nowrap">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No hay resultados.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-2">
        <span className="text-xs sm:text-sm text-muted-foreground">
          Página {page} de {totalPages}
        </span>
        <div className="flex flex-wrap gap-1 sm:gap-2 justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1 || isLoading}
            className="text-xs sm:text-sm px-2 sm:px-3"
          >
            Anterior
          </Button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(
              (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2,
            )
            .map((p, idx, arr) => {
              // Mostrar '...' si hay salto grande
              const prev = arr[idx - 1];
              if (prev && p - prev > 1) {
                return (
                  <span
                    key={`dots-${p}`}
                    className="px-0.5 sm:px-1 text-muted-foreground text-xs sm:text-sm"
                  >
                    ...
                  </span>
                );
              }
              return (
                <Button
                  key={p}
                  variant={p === page ? "headerAction" : "outline"}
                  size="sm"
                  onClick={() => onPageChange(p)}
                  disabled={isLoading}
                  className="min-w-[1.75rem] sm:min-w-[2rem] px-1.5 sm:px-2 text-xs sm:text-sm"
                >
                  {p}
                </Button>
              );
            })}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages || isLoading}
            className="text-xs sm:text-sm px-2 sm:px-3"
          >
            Siguiente
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DataTable;
